import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { listPendingDrafts, publishAllPending } from "@/lib/pages.functions";

const DISMISS_KEY = "glg_publish_bar_dismissed_until";

/**
 * Floating bar shown only to signed-in admins. Surfaces unpublished content
 * drafts with a one-click publish, and explains the separate code-deploy step
 * so it's obvious why preview can differ from the live site.
 */
export function AdminPublishBar() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [dismissed, setDismissed] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const uid = sessionData.session?.user?.id;
      if (!uid) return;
      const { data } = await supabase.rpc("has_role", { _user_id: uid, _role: "admin" });
      if (!active) return;
      setIsAdmin(!!data);
      const until = Number(localStorage.getItem(DISMISS_KEY) ?? 0);
      setDismissed(Date.now() < until);
    })();
    return () => {
      active = false;
    };
  }, []);

  const listFn = useServerFn(listPendingDrafts);
  const publishFn = useServerFn(publishAllPending);
  const qc = useQueryClient();

  const { data: pending = [] } = useQuery({
    queryKey: ["pending-drafts"],
    queryFn: () => listFn(),
    enabled: isAdmin,
    refetchInterval: 60_000,
  });

  const publishMutation = useMutation({
    mutationFn: () => publishFn(),
    onSuccess: (res) => {
      toast.success(
        res.published.length
          ? `Published ${res.published.length} page${res.published.length === 1 ? "" : "s"}`
          : "Nothing pending to publish",
      );
      qc.invalidateQueries({ queryKey: ["pending-drafts"] });
      qc.invalidateQueries({ queryKey: ["admin-pages"] });
      qc.invalidateQueries({ queryKey: ["admin-pages-builder"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Publish failed"),
  });

  if (!isAdmin || dismissed) return null;

  function dismissForADay() {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    setDismissed(true);
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center p-3 print:hidden">
      <div className="pointer-events-auto w-full max-w-3xl rounded-2xl border border-border bg-card/95 p-3 shadow-lg backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold">
              {pending.length > 0
                ? `${pending.length} page${pending.length === 1 ? "" : "s"} with unpublished content`
                : "All content is published"}
            </p>
            <p className="text-xs text-muted-foreground">
              You publish content, menus, theme and new pages yourself — it goes live instantly. Only layout/code work
              needs a deploy.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setExpanded((v) => !v)}
              className="rounded-full border border-border bg-background px-3 py-1.5 text-xs hover:bg-secondary"
            >
              {expanded ? "Hide details" : "Details"}
            </button>
            <Link
              to="/admin"
              search={{ tab: "pages" as const }}
              className="rounded-full border border-border bg-background px-3 py-1.5 text-xs hover:bg-secondary"
            >
              Open Pages
            </Link>
            <button
              onClick={() => publishMutation.mutate()}
              disabled={publishMutation.isPending || pending.length === 0}
              className="rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {publishMutation.isPending ? "Publishing…" : "Publish all content"}
            </button>
            <button
              onClick={dismissForADay}
              aria-label="Hide publish bar for 24 hours"
              className="rounded-full border border-border bg-background px-2 py-1.5 text-xs hover:bg-secondary"
            >
              ✕
            </button>
          </div>
        </div>

        {expanded && (
          <div className="mt-3 space-y-2 rounded-xl border border-border bg-background p-3 text-xs">
            {pending.length === 0 ? (
              <p className="text-muted-foreground">No page has a staged draft right now.</p>
            ) : (
              <ul className="space-y-1">
                {pending.map((p) => (
                  <li key={p.slug} className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium">{p.title}</span>
                    <span className="text-muted-foreground">
                      {p.changedFields.length ? p.changedFields.join(", ") : "draft saved"} ·{" "}
                      {new Date(p.updated_at).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-muted-foreground">
              Admin-owned and instant: page text, images, SEO, builder blocks, header/footer menus (Admin →
              Navigation), theme, and brand-new pages you create in Navigation. Only structural code work — new
              components or layout changes — ships from the Lovable editor Publish button.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
